import React, { type FunctionComponent, useEffect } from 'react'
import { useParams } from 'react-router'
import { injectIntl } from 'react-intl'
import { type InjectedDashIntlProps } from 'intl/intl.types'
import { useTrackEvent } from 'utils/events/hooks'
import { type RetailerScopedRouteParams } from 'legacy/common/types'
import { toInteger } from 'utils/parsing/numbers'
import { StoreGroupsEditContent } from 'legacy/sections/store-groups/components/edit/StoreGroupsEditContent'
import { useGoToStoreGroupsList } from 'legacy/sections/store-groups/utils/navigation.hooks'
import { useUpdateStoreGroupOnSubmit } from 'legacy/sections/store-groups/components/edit/utils/submitForm'
import { useFetchStoreGroup } from 'legacy/sections/store-groups/graphql/fetch.hooks'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'

interface QueryParams extends RetailerScopedRouteParams {
  storeGroupId: string
}

interface Props extends InjectedDashIntlProps, RetailerRouteComponentProps {}

const _StoreGroupsEdit: FunctionComponent<Props> = ({ intl }) => {
  const { storeGroupId } = useParams<QueryParams>()
  useTrackEvent({
    id: 'settings.store_groups.edit.viewed',
    description: 'settings.store_groups.edit.viewed',
    data: {
      storeGroupId: toInteger(storeGroupId),
    },
  })

  const { storeGroup, loading } = useFetchStoreGroup({ storeGroupId })
  const goToStoreGroupsList = useGoToStoreGroupsList()
  const { handleSubmit, updatedStoreGroup, error } = useUpdateStoreGroupOnSubmit()
  const { notify, notifyError } = useNotifications()

  // Redirect back to the store groups list page after update
  useEffect(() => {
    if (!updatedStoreGroup) return

    notify(
      intl.formatMessage(
        { id: 'settings.storeGroups.notifications.updateSuccess' },
        { name: updatedStoreGroup.name }
      )
    )
    goToStoreGroupsList()
  }, [goToStoreGroupsList, intl, notify, updatedStoreGroup])

  // Redirect back to the store groups list page after error
  useEffect(() => {
    if (!error) return

    notifyError(intl.formatMessage({ id: 'settings.storeGroups.notifications.updateError' }))
    goToStoreGroupsList()
  }, [error, goToStoreGroupsList, intl, notifyError])

  return (
    <StoreGroupsEditContent
      storeGroup={storeGroup}
      loading={loading}
      error={error}
      handleSubmit={handleSubmit}
    />
  )
}

export const StoreGroupsEdit = injectIntl(_StoreGroupsEdit)
